'use client';

import { type CSSProperties, type ReactNode } from 'react';
import {
  AlertCircleIcon,
  CheckmarkCircle01Icon,
  InformationCircleIcon,
  Alert01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { Icon } from '../../components/icon';
import { Toast } from '../../components/toast';
import { Avatar } from '../../components/avatar';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';

const TOAST_NO_DRAG_STYLE = {
  WebkitAppRegion: 'no-drag',
} as CSSProperties;

type ToastMeta = {
  avatar?: {
    name: string;
    src?: string;
  };
  render?: ReactNode;
};

export const toasterApi: ReturnType<typeof Toast.createToaster> =
  Toast.createToaster({
    placement: 'top-end',
    overlap: true,
    gap: 16,
  });
export function ToasterProvider() {
  return (
    <Toast.Toaster toaster={toasterApi}>
      {(toast) => {
        const meta = toast.meta as ToastMeta | undefined;

        if (meta?.render) {
          return (
            <Box key={toast.id} style={TOAST_NO_DRAG_STYLE}>
              {meta.render}
            </Box>
          );
        }

        const hasTitle = Boolean(toast.title);
        const iconMarginTop = hasTitle ? '0.5' : undefined;

        return (
          <Toast.Root key={toast.id} style={TOAST_NO_DRAG_STYLE}>
            <HStack
              gap="3"
              width="full"
              alignItems={hasTitle ? 'start' : 'center'}
              justifyContent="space-between"
            >
              {meta?.avatar ? (
                <Avatar
                  name={meta.avatar.name}
                  src={meta.avatar.src}
                  size="md"
                />
              ) : toast.type === 'error' ? (
                <Icon color="red.9" mt={iconMarginTop}>
                  <AlertCircleIcon />
                </Icon>
              ) : toast.type === 'success' ? (
                <Icon color="green.9" mt={iconMarginTop}>
                  <CheckmarkCircle01Icon />
                </Icon>
              ) : toast.type === 'warning' ? (
                <Icon color="orange.9" mt={iconMarginTop}>
                  <Alert01Icon />
                </Icon>
              ) : toast.type === 'info' ? (
                <Icon color="blue.9" mt={iconMarginTop}>
                  <InformationCircleIcon />
                </Icon>
              ) : null}
              <VStack flex={1} alignItems="start" gap="1" minW={0} pr={2}>
                {toast.title && (
                  <Toast.Title data-type={toast.type}>
                    {toast.title}
                  </Toast.Title>
                )}
                {toast.description && (
                  <Toast.Description data-type={toast.type}>
                    {toast.description}
                  </Toast.Description>
                )}
                {toast.action?.label && toast.action.onClick && (
                  <Toast.ActionTrigger asChild alignSelf={'end'}>
                    <Button onClick={toast.action.onClick} variant="link">
                      {toast.action.label}
                    </Button>
                  </Toast.ActionTrigger>
                )}
              </VStack>
              <Toast.CloseTrigger asChild>
                <IconButton size="sm" variant="link" flexShrink={0}>
                  <Cancel01Icon />
                </IconButton>
              </Toast.CloseTrigger>
            </HStack>
          </Toast.Root>
        );
      }}
    </Toast.Toaster>
  );
}
